import React, {useEffect, useState} from 'react'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import {LoginData} from 'organization/Auth/Interfaces/login'
import {RegisterData} from 'organization/Auth/Interfaces/register'

import {TeamMember} from 'organization/models/TeamMember'
import {
  organizationAccessTokenkey,
  organizationApiClient,
} from 'organization/organizationApiClient'
import {toast} from 'react-toastify'
import {UpdateProfileData} from 'organization/Auth/Interfaces/update_profile'
import {UpdatePasswordData} from 'organization/Auth/Interfaces/update_password'
import {
  PasswordRecoverData,
  PasswordRecoverUpdateData,
} from 'organization/Auth/Interfaces/password_recover'
import {getFbc} from 'FacebookPixelProvider'

export interface OrganizationAuthToken {
  access_token: string
  token_type: string
  expires_in: number
}

export type OrganizationAuthRequiresVerification = {
  requiresVerification: true
  email: string
}

export type OrganizationAuthResult =
  | true
  | false
  | OrganizationAuthRequiresVerification

export interface OrganizationAuthContextProps {
  user: TeamMember | null
  login: (data: LoginData) => Promise<OrganizationAuthResult>
  register: (data: RegisterData) => Promise<OrganizationAuthResult>
  updateProfile: (data: UpdateProfileData) => Promise<string | boolean>
  updatePassword: (data: UpdatePasswordData) => Promise<string | boolean>
  passwordRecover: (data: PasswordRecoverData) => Promise<string | boolean>
  passwordReset: (
    data: PasswordRecoverUpdateData,
    token: string,
    email: string,
  ) => Promise<string | boolean>
  logout: () => Promise<string | boolean>
  googleAuth: (promoInviteToken?: string | null) => Promise<boolean>
  googleCallback: (callback: string) => Promise<boolean>
  saveAccessToken: (token: string) => void
  refreshUser: () => Promise<boolean>
  resendVerificationEmail: (email: string) => Promise<boolean>
}

export const OrganizationAuthContext = React.createContext<
  OrganizationAuthContextProps | undefined
>(undefined)

export default function OrganizationAuthProvider(props: {
  children: React.ReactElement
}) {
  const [user, setUser] = useState<TeamMember | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const apiClient = organizationApiClient()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveToken = (token: OrganizationAuthToken) => {
    localStorage.setItem(organizationAccessTokenkey, token.access_token)
  }

  const get = async (): Promise<boolean> => {
    try {
      const result = await apiClient
        .get('/api/organization/user')
        .catch(() => false)

      if (result) {
        setUser(result)
        return true
      }
      return false
    } finally {
      setLoading(false)
    }
  }

  const register = async (
    data: RegisterData,
  ): Promise<OrganizationAuthResult> => {
    try {
      const {accept_terms: _accept, ...rest} = data
      const body: Record<string, unknown> = {...rest}
      if (!body.promo_invite_token) {
        delete body.promo_invite_token
      }
      const response: any = await apiClient.post(
        '/api/organization/register?fbc=' + getFbc(),
        body,
      )

      if (response?.requires_verification) {
        return {requiresVerification: true, email: response.email}
      }

      saveToken(response as OrganizationAuthToken)
      return (await get()) === true
    } catch (error: any) {
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  const login = async (data: LoginData): Promise<OrganizationAuthResult> => {
    try {
      const body: Record<string, unknown> = {
        email: data.email,
        password: data.password,
        remember: data.remember,
      }
      const invite = data.promo_invite_token?.trim()
      if (invite && /^[a-f0-9]{64}$/i.test(invite)) {
        body.promo_invite_token = invite.toLowerCase()
      }
      const response: any = await apiClient.post(
        '/api/organization/login?fbc=' + getFbc(),
        body,
      )
      saveToken(response as OrganizationAuthToken)
      return (await get()) === true
    } catch (error: any) {
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.code === 'email_not_verified'
      ) {
        return {
          requiresVerification: true,
          email: error.response.data.email,
        }
      }
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  const resendVerificationEmail = async (email: string) => {
    try {
      await apiClient.post('/api/organization/email/verification-notification', {
        email,
      })
      return true
    } catch (error: any) {
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  const saveAccessToken = (token: string) => {
    localStorage.setItem(organizationAccessTokenkey, token)
  }

  const updateProfile = async (data: UpdateProfileData) => {
    const result = await apiClient
      .post('api/organization/update-profile', data)
      .then(setUser)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const updatePassword = async (data: UpdatePasswordData) => {
    const result = await apiClient
      .post('api/organization/update-password', data)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const passwordRecover = async (data: PasswordRecoverData) => {
    const result = await apiClient
      .post('api/organization/password-recover', data)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const passwordReset = async (
    data: PasswordRecoverUpdateData,
    token: string,
    email: string,
  ) => {
    try {
      const response: any = await apiClient.post('api/organization/password-reset', {
        ...data,
        token: token,
        email: email,
      })
      saveToken(response as OrganizationAuthToken)
      return (await get()) === true
    } catch (error: any) {
      toast.warning(error.response.data.message)
      return false
    }
  }

  const logout = async () => {
    try {
      await apiClient.post('/api/organization/logout')
      setUser(null)
      localStorage.setItem(organizationAccessTokenkey, '')
      return true
    } catch (error: any) {
      toast.warning(error.message)
      return error.message
    }
  }

  const googleAuth = async (promoInviteToken?: string | null) => {
    const token = promoInviteToken?.trim()
    const params = new URLSearchParams()
    params.set('fbc', getFbc() ?? '')
    if (token && /^[a-f0-9]{64}$/i.test(token)) {
      params.set('invite', token.toLowerCase())
    }
    const result = await apiClient
      .get(`api/organization/google/auth?${params.toString()}`)
      .then((data) => {
        window.location.href = data.url

        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const googleCallback = async (callback: string) => {
    try {
      const fbc = getFbc() ?? ''
      const sep = callback.includes('?') ? '&' : '?'
      const tokenData = (await apiClient.get(
        `/api/organization/google/callback${callback}${sep}fbc=${encodeURIComponent(fbc)}`,
      )) as OrganizationAuthToken
      if (!tokenData?.access_token) {
        return false
      }
      saveToken(tokenData)
      void get()
      return true
    } catch (error: any) {
      toast.warning(error?.response?.data?.message)
      return false
    }
  }

  return (
    <OrganizationAuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateProfile,
        updatePassword,
        passwordRecover,
        passwordReset,
        googleAuth,
        googleCallback,
        saveAccessToken,
        refreshUser: get,
        resendVerificationEmail,
      }}
    >
      {loading ? <FullPageLoader /> : props.children}
    </OrganizationAuthContext.Provider>
  )
}

export function useOrganizationAuth() {
  const context = React.useContext(OrganizationAuthContext)
  if (context === undefined) {
    throw new Error(
      'useOrganizationAuth must be used within OrganizationAuthProvider',
    )
  }
  return context
}
